import React, { useId, useRef, useState } from 'react';
import { ImagePlus, Trash2, Upload } from 'lucide-react';
import {
  AppButton, DropZone, FieldError, HelpText, Input, Label,
} from '../ui';

// ─── ImageField — one website image: upload, drop, or paste a link ───────
//
// The website stores an image either as an uploaded file or as a pasted
// link; this control offers both and previews whichever is set. It does not
// upload anything itself: the owner handles `onFile` and passes `busy` while
// it works, because the two stores it serves (page content and the header /
// footer settings) keep the result in different shapes.
//
// Props:
//   label, hint, error — as on FormField
//   previewUrl — what to show; '' when nothing is set
//   urlValue   — the pasted link ('' when the image is an upload)
//   onUrlChange(url) · onFile(file) · onRemove()
//   busy       — an upload is in flight
//   compact    — the small form, for rows inside a list

const IMAGE_TYPES = /^image\//;

export default function ImageField({
  label,
  hint,
  error,
  previewUrl = '',
  urlValue = '',
  onUrlChange,
  onFile,
  onRemove,
  busy = false,
  compact = false,
}) {
  const id = useId();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [typeError, setTypeError] = useState('');

  const accept = (file) => {
    if (!file) return;
    if (!IMAGE_TYPES.test(file.type)) {
      setTypeError('Choose an image file — JPG, PNG, WebP or SVG.');
      return;
    }
    setTypeError('');
    onFile(file);
  };

  const pick = () => inputRef.current?.click();
  const shownError = typeError || error;
  const labelId = `${id}-label`;
  const urlId = `${id}-url`;
  const describedBy = [shownError && `${id}-error`, hint && !shownError && `${id}-hint`]
    .filter(Boolean).join(' ') || undefined;

  return (
    <div role="group" aria-labelledby={label ? labelId : undefined} className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5">
      {label && <Label id={labelId} htmlFor={urlId}>{label}</Label>}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="tw-hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => { accept(e.target.files?.[0]); e.target.value = ''; }}
      />

      {previewUrl ? (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <img
            src={previewUrl}
            alt=""
            className={
              compact
                ? 'tw-h-12 tw-w-20 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-object-contain'
                : 'tw-h-24 tw-max-w-[220px] tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-object-contain'
            }
          />
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <AppButton size="sm" variant="outline" onClick={pick} disabled={busy}>
              <Upload size={16} /> {busy ? 'Uploading…' : 'Replace'}
            </AppButton>
            <AppButton size="sm" variant="ghost-destructive" onClick={onRemove} disabled={busy}>
              <Trash2 size={16} /> Remove
            </AppButton>
          </div>
        </div>
      ) : (
        <DropZone
          size="sm"
          state={dragOver ? 'active' : 'idle'}
          disabled={busy}
          onClick={pick}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); accept(e.dataTransfer.files?.[0]); }}
        >
          <ImagePlus size={18} className="tw-text-content-subtle group-hover:tw-text-primary" aria-hidden="true" />
          <span className="tw-text-sm tw-font-medium tw-text-content">{busy ? 'Uploading…' : 'Upload an image'}</span>
          {!compact && !busy && <span className="tw-text-sm tw-text-content-subtle">or drop one here</span>}
        </DropZone>
      )}

      <Input
        id={urlId}
        size="sm"
        type="url"
        spellCheck={false}
        placeholder="…or paste an image link (https://…)"
        value={urlValue}
        onChange={(e) => onUrlChange(e.target.value)}
        aria-describedby={describedBy}
        aria-invalid={shownError ? true : undefined}
      />

      {shownError
        ? <FieldError id={`${id}-error`}>{shownError}</FieldError>
        : hint && <HelpText id={`${id}-hint`}>{hint}</HelpText>}
    </div>
  );
}
